import { types, getEnv, flow, SnapshotOut } from 'mobx-state-tree';
import { observable } from 'mobx';
import { ProgressStateModel, ProgressStateModelForStore } from '../progressStateModel';
import { ICommonAppEnvWithSBBaseApi } from '../index';
import { IGroupedApiCtx } from '../../api/apiCommon';
import { TicketSystemsAndCombinations } from '../../api/proto/apiContract';
import { ISportsBettingBaseApi, errorIsCancel } from '../../api/apiBase';

const TicketCombinationItem = types
    .model('ticketCombinationItemStore', {
        name: types.string,
        rate: types.maybeNull(types.number),
        stake: types.maybeNull(types.number),
        potentialWin: types.maybeNull(types.number),
    })

const TicketBetItem = types.model(
    'ticketBetItemStore',
    {
        combinationCount: types.number,
        potentialWinMin: types.number,
        potentialWinMax: types.number,
        selectedSystem: types.number,
        systemStake: types.number,
        unitStake: types.number,
        selectionCount: types.number,
        systemOutOf: types.number,
        combinations: types.array(TicketCombinationItem),
    }
)

const PrizePotentialWithMistakesItem = types.model(
    'prizePotentialWithMistakesItem',
    {
        numberOfMistakes: types.number,
        prizePotentialMax: types.number,
        prizePotentialMin: types.number
    }
)

export const CalculationsBaseStore = types
    .model('ticketCombinationBaseStore', {
        selectionsCount: types.optional(types.number, 0),

        bets: types.optional(types.array(TicketBetItem), []),
        potentialWinWithErrors: types.optional(types.array(PrizePotentialWithMistakesItem), []),

        isCalculationExpanded: types.optional(types.boolean, false),

        state: types.optional(ProgressStateModelForStore, ProgressStateModel.NotInitialized),
        hasData: types.optional(types.boolean, false)
    })
    .actions(self => {
        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);
        let synchroFullLoadCtx: IGroupedApiCtx<ISportsBettingBaseApi> | null = null;

        const loadDataAsync: (loader: (api: ISportsBettingBaseApi) => Promise<TicketSystemsAndCombinations>) => Promise<void> = flow(function* (loader: (api: ISportsBettingBaseApi) => Promise<TicketSystemsAndCombinations>) {
            if (synchroFullLoadCtx && synchroFullLoadCtx.isOpen()) {
                synchroFullLoadCtx.cancel();
                synchroFullLoadCtx = null;
            }

            self.state = ProgressStateModel.Loading;
            let ctx: IGroupedApiCtx<ISportsBettingBaseApi> | null = null;

            try {

                ctx = env.api.open();
                synchroFullLoadCtx = ctx;

                let r: TicketSystemsAndCombinations = yield loader(ctx.api);
                self.bets = observable([]);
                r.Items.forEach(bet => {
                    self.bets.push(
                        TicketBetItem.create(<SnapshotOut<typeof TicketBetItem>>{
                            combinationCount: bet.CombinationCount,
                            potentialWinMax: bet.PrizeEvalPotentionalValueMax,
                            potentialWinMin: bet.PrizeEvalPotentionalValueMin,
                            selectedSystem: bet.SelectedSystem,
                            selectionCount: self.selectionsCount,
                            systemStake: bet.SystemStake,
                            unitStake: bet.UnitStake,
                            systemOutOf: bet.SystemOutOf,
                            combinations: observable(bet.Combinations.map(combination => {
                                return TicketCombinationItem.create(<SnapshotOut<typeof TicketCombinationItem>>{
                                    name: combination.Name,
                                    potentialWin: combination.PotentialWin,
                                    rate: combination.Rate,
                                    stake: combination.Stake
                                })
                            }))
                        })
                    )
                });

                self.potentialWinWithErrors = observable([]);
                r.PrizePotentialWithMistakes.forEach(winWithErrors => {
                    self.potentialWinWithErrors.push(
                        PrizePotentialWithMistakesItem.create(<SnapshotOut<typeof PrizePotentialWithMistakesItem>>{
                            numberOfMistakes: winWithErrors.NumberOfMistakes ? winWithErrors.NumberOfMistakes : 0,
                            prizePotentialMax: winWithErrors.PrizePotentialMax,
                            prizePotentialMin: winWithErrors.PrizePotentialMin
                        })
                    )
                })

                self.hasData = true;
                self.state = ProgressStateModel.Success;
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                env.loggingService.error(error);
                self.state = ProgressStateModel.Failed;
                throw error;
            } finally {
                if (synchroFullLoadCtx === ctx) {
                    synchroFullLoadCtx = null;
                }
            }
        })

        const toggleExpandCalculations = () => { }

        return {
            toggleExpandCalculations,
            loadDataAsync
        }
    });

export const CalculationsForMyTicketStore = types.compose(
    CalculationsBaseStore,
    types.model({
        ticketId: types.string
    })).actions((self) => {

        const loader = (api: ISportsBettingBaseApi): Promise<TicketSystemsAndCombinations> => {
            return api.getTicketSystemCombinationsAsync({ id: self.ticketId })
        }

        const toggleExpandCalculations = () => {
            if (!self.isCalculationExpanded && !self.hasData) {
                self.loadDataAsync(loader);
            }
            self.isCalculationExpanded = !self.isCalculationExpanded;
        }

        return {
            toggleExpandCalculations
        }
    });

export const CalculationsForPublicTicketStore = types.compose(
    CalculationsBaseStore,
    types.model({
        publicTicketId: types.optional(types.string, "")
    })).actions((self) => {

        const loader = (api: ISportsBettingBaseApi): Promise<TicketSystemsAndCombinations> => {
            return api.getTicketSystemCombinationsByPublicIdAsync({ publicId: self.publicTicketId })
        }

        const setPublicId = (value: string): void => {
            self.publicTicketId = value;
        }

        const toggleExpandCalculations = () => {
            if (!self.isCalculationExpanded && !self.hasData) {
                self.loadDataAsync(loader);
            }
            self.isCalculationExpanded = !self.isCalculationExpanded;
        }

        return {
            toggleExpandCalculations,
            setPublicId
        }
    });
